export const formatUnderScoreText = (text: string): string => {
   return text
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
};

/**
 * Removes null, undefined, or empty keys from an object
 * @param obj Object to clean
 * @param options Configuration options
 * @returns New object with filtered keys
 * @author Cedric Mutesa
 */
export const removeEmptyKeys = <T extends Record<string, any>>(
   obj: T,
   options: {
      removeNull?: boolean; // Remove null and undefined values
      removeEmpty?: boolean; // Remove empty strings
      removeEmptyArrays?: boolean; // Remove empty arrays
      removeEmptyObjects?: boolean; // Remove empty objects
   } = { removeNull: true, removeEmpty: false, removeEmptyArrays: false, removeEmptyObjects: false },
): Partial<T> => {
   const { removeNull, removeEmpty, removeEmptyArrays, removeEmptyObjects } = options;

   return Object.entries(obj).reduce((acc, [key, value]) => {
      // Skip null/undefined values if configured
      if (removeNull && (value === null || value === undefined)) {
         return acc;
      }

      // Skip empty strings if configured
      if (removeEmpty && value === '') {
         return acc;
      }

      // Skip empty arrays if configured
      if (removeEmptyArrays && Array.isArray(value) && value.length === 0) {
         return acc;
      }

      // Skip empty objects if configured
      if (
         removeEmptyObjects &&
         typeof value === 'object' &&
         value !== null &&
         !Array.isArray(value) &&
         Object.keys(value).length === 0
      ) {
         return acc;
      }

      // Keep all other values
      return { ...acc, [key]: value };
   }, {} as Partial<T>);
};

/**
 * Builds query parameters string from an object with advanced options
 * @param params Object containing query parameters
 * @param options Configuration options
 * @returns Formatted query string (without the leading '?')
 * @author Cedric Mutesa
 */
export const buildQueryParams = (
   params: Record<string, any>,
   options: {
      skipNull?: boolean; // Skip null and undefined values
      skipEmpty?: boolean; // Skip empty strings
      arrayFormat?: 'comma' | 'bracket' | 'repeat'; // How to format array values
   } = { skipNull: true, skipEmpty: true, arrayFormat: 'repeat' },
) => {
   const { skipNull, skipEmpty, arrayFormat } = options;

   // Remove empty keys based on options
   params = removeEmptyKeys(params, {
      removeNull: skipNull,
      removeEmpty: skipEmpty,
   });
   return Object.entries(params)
      .filter(([, value]) => {
         if (skipNull && (value === null || value === undefined)) return false;
         if (skipEmpty && value === '') return false;
         return true;
      })
      .map(([key, value]) => {
         // Handle arrays based on specified format
         if (Array.isArray(value)) {
            if (arrayFormat === 'comma') {
               return `${encodeURIComponent(key)}=${value.map(encodeURIComponent).join(',')}`;
            } else if (arrayFormat === 'bracket') {
               return `${encodeURIComponent(key)}[]=${value.map(encodeURIComponent).join('&' + encodeURIComponent(key) + '[]=')}`;
            } else {
               // repeat (default)
               return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
            }
         }

         // Handle objects by converting to JSON string
         if (value !== null && typeof value === 'object') {
            return `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`;
         }

         // Handle primitive values
         return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .join('&');
};